import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import { ArrowLeft, FileText, Tag, Calendar, ExternalLink, CheckCircle2, Clock, XCircle } from 'lucide-react';

interface Requisition {
  _id: string;
  position: string;
  department: string;
  status: string;
  createdAt: string;
  closedAt?: string;
}

const RequisitionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [req, setReq] = useState<Requisition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequisition = async () => {
      try {
        const res = await api.get(`/requisitions/${id}`);
        setReq(res.data);
      } catch (err) {
        setError('Заявка не найдена.');
      } finally {
        setLoading(false);
      }
    };
    fetchRequisition();
  }, [id]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка...</div>;
  if (error) return <div style={{ padding: 60, textAlign: 'center', color: '#ff4d4f' }}>{error}</div>;
  if (!req) return null;

  const statusConfig: Record<string, { label: string, color: string, bg: string, icon: React.ReactNode }> = {
    pending: { label: 'На рассмотрении', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: <Clock size={18} /> },
    approved: { label: 'Утверждена', color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: <CheckCircle2 size={18} /> },
    rejected: { label: 'Отклонена', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: <XCircle size={18} /> },
    draft: { label: 'Черновик', color: '#64748b', bg: 'rgba(100,116,139,0.1)', icon: <FileText size={18} /> },
    closed: { label: 'Закрыта', color: '#64748b', bg: 'rgba(100,116,139,0.1)', icon: <CheckCircle2 size={18} /> },
  };
  const conf = statusConfig[req.status] || statusConfig.draft;

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate('/app/recruitment-analytics/requisitions')} style={styles.backBtn}>
          <ArrowLeft size={16} /> Назад к заявкам
        </button>
        <button onClick={() => navigate(`/app/hr/requisition/${req._id}`)} style={{ ...styles.backBtn, borderColor: '#10b981', color: '#10b981' }}>
          <ExternalLink size={16} /> Открыть в CRM
        </button>
      </div>

      <div style={{ display: 'flex', gap: 24 }}>
        <div style={{ ...styles.glassCard, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: '0 0 8px 0' }}>{req.position}</h1>
              <div style={{ display: 'flex', gap: 16, color: '#64748b', fontSize: 14 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Tag size={16} /> {req.department || 'Без отдела'}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={16} /> {new Date(req.createdAt).toLocaleDateString('ru-RU')}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 20, background: conf.bg, color: conf.color, fontWeight: 600 }}>
              {conf.icon} {conf.label}
            </div>
          </div>
          
          <div style={{ padding: 20, background: 'rgba(255,255,255,0.4)', borderRadius: 16, border: '1px solid rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', margin: '0 0 12px 0' }}>Информация о заявке</h3>
            <p style={{ color: '#4a4a68', margin: 0 }}>Заявка на подбор персонала для позиции "{req.position}" в отделе {req.department}. Статус: {conf.label.toLowerCase()}.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, padding: 32, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)' },
  backBtn: { background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)', border: '1px solid #2575fc', color: '#2575fc', borderRadius: 30, padding: '8px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 14 },
};

export default RequisitionDetail;
